import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import DatePicker from '../../../components/DatePicker'
import TransactionHistoryTable from '../components/TransactionHistoryTable'
import goalResultService from '../../../services/transactionService'
import { MdDataSaverOff } from 'react-icons/md'
import { useWindowWidth } from '@react-hook/window-size'

const typeFilters = [
  { label: 'All', value: 'all' },
  { label: 'Deposit', value: 'deposit' },
  { label: 'Withdrawal', value: 'withdraw' },
]

const statusFilters = [
  { label: 'All', value: 'all' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' }
]

const rangeFilters = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'This month', value: 'month' },
 // { label: 'This year', value: 'year' },
 // { label: 'Custom range', value: 'custom' },
]

const API_DATE_FORMAT = 'YYYY-MM-DD';

const TransactionHistoryPage = () => {
  const onlyWidth = useWindowWidth()
  const isMobile = onlyWidth <= 768;

  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [typeFilter, setTypeFilter] = useState(typeFilters[0])
  const [statusFilter, setStatusFilter] = useState(statusFilters[0])
  const [rangeFilter, setRangeFilter] = useState(rangeFilters[0])
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState(() =>
    moment().subtract(7, 'days').format(API_DATE_FORMAT),
  )
  const [endDate, setEndDate] = useState(() =>
    moment().format(API_DATE_FORMAT),
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    fetchHistory()
  }, [])

  const fetchHistory = async ({ start = startDate, end = endDate } = {}) => {
    try {
      setLoading(true)
      const payload = {
        userId: 1,
        rawOnly: true
      }

      if (start) {
        payload.startDate = start
      }

      if (end) {
        payload.endDate = end
      }

      const response = await goalResultService.getTransactionHistory(payload)

      if (response.status !== 200) {
        throw new Error('Unable to fetch data')
      }

      if (!response.data?.payload) {
        throw new Error('Invalid response from server')
      }
      const merged = response.data.payload.map((item) => {
          const type = item.transactiontypeId === 1 ? 'deposit' : 'withdraw'
          const createdMoment = moment(item.createdDate ?? item.createdAt)
          const updatedMoment = moment(item.updatedDate ?? item.updatedAt)

          return {
            id: item.transactionId,
            transactionNo: item.transactionNo,
            createdDate: createdMoment.format('DD MMM YYYY, HH:mm A'),
            updatedDate: updatedMoment.format('DD MMM YYYY, HH:mm A'),
            createdMoment,
            updatedMoment,
            bankType: item.agentBankType,
            remark: item.agentRemark,
            amount: item.amount,
            userName: item.username,
            currency: item.currency ?? 'MMK',
            type,
            status:
              item.status?.toLowerCase() ??
              (type === 'deposit' ? 'completed' : 'completed'),
            accountName:
              type === 'deposit' ? item.agentAccountName : item.bankAccountName,
            accountNo:
              type === 'deposit' ? item.agentAccountNo : item.bankAccountNo,
          }
      })
      console.log('Fetched transactions:', merged)

      setTransactions(
        merged.sort((a, b) => b.createdMoment.valueOf() - a.createdMoment.valueOf()),
      )
    } catch (error) {
      toast.error('Unable to load transaction history right now.')
      setTransactions([])
    } finally {
      setLoading(false)
    }
  }

  const filteredTransactions = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    return transactions.filter((item) => {
      if (typeFilter.value !== 'all' && item.type !== typeFilter.value) {
        return false
      }

      if (statusFilter.value !== 'all' && item.status !== statusFilter.value) {
        return false
      }

      if (rangeFilter.value !== 'all') {
        if (rangeFilter.value === 'year') {
          if (item.createdMoment.year() !== moment().year()) {
            return false
          }
        } else if (rangeFilter.value === 'month') {
          if (!item.createdMoment.isSame(moment(), 'month')) {
            return false
          }
        } else if (rangeFilter.value === 'custom') {
          if (startDate && item.createdMoment.isBefore(moment(startDate).startOf('day'))) {
            return false
          }

          if (endDate && item.createdMoment.isAfter(moment(endDate).endOf('day'))) {
            return false
          }
        } else {
          const days = Number(rangeFilter.value)
          if (!Number.isNaN(days) && item.createdMoment.isBefore(moment().subtract(days, 'days'))) {
            return false
          }
        }
      }

      if (!keyword) {
        return true
      }

      return (
        item.transactionNo?.toLowerCase().includes(keyword) ||
        item.accountName?.toLowerCase().includes(keyword) ||
        item.userName?.toLowerCase().includes(keyword)
      )
    })
  }, [transactions, typeFilter, statusFilter, rangeFilter, search, startDate, endDate])

  const paginatedTransactions = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    return filteredTransactions.slice(startIndex, startIndex + pageSize)
  }, [filteredTransactions, page, pageSize])

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / pageSize))
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [filteredTransactions, page, pageSize])

  const handlePageChange = (nextPage) => {
    const safeNext = Math.max(1, nextPage)
    if (safeNext !== page) {
      setPage(safeNext)
    }
  }

  const handlePageSizeChange = (nextSize) => {
    if (nextSize !== pageSize) {
      setPageSize(nextSize)
      setPage(1)
    }
  }

  const handleTypeFilterChange = (filter) => {
    setTypeFilter(filter)
    setPage(1)
  }

  const handleStatusFilterChange = (filter) => {
    setStatusFilter(filter)
    setPage(1)
  }

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
    setPage(1)
  }

  const handleDateRangeChange = (filter) => {
    setRangeFilter(filter)
    setPage(1)

    if (filter.value === 'custom') {
      setStartDate('')
      setEndDate('')
      return
    }

    if (filter.value === 'all') {
      setStartDate('')
      setEndDate('')
      fetchHistory({ start: '', end: '' })
      return
    }

    const today = moment()
    const nextEnd = today.format(API_DATE_FORMAT)
    let nextStart = ''

    if (filter.value === 'year') {
      nextStart = today.clone().startOf('year').format(API_DATE_FORMAT)
    } 
    else if (filter.value === 'month') {
      nextStart = today.clone().startOf('month').format(API_DATE_FORMAT)
    }
    else {
      const days = Number(filter.value)
      if (!Number.isNaN(days) && days > 0) {
        nextStart = today.clone().subtract(days - 1, 'days').format(API_DATE_FORMAT)
      }
    }

    setStartDate(nextStart)
    setEndDate(nextEnd)
    fetchHistory({ start: nextStart, end: nextEnd })
  }

  const handleCustomDateChange = (field, value) => {
    const nextStart = field === 'start' ? value : startDate
    const nextEnd = field === 'end' ? value : endDate

    setStartDate(nextStart)
    setEndDate(nextEnd)

    if (!nextStart || !nextEnd) {
      return
    }

    if (moment(nextStart).isAfter(moment(nextEnd))) {
      toast.error('Start date cannot be later than end date.')
      return
    }

    setPage(1)
    fetchHistory({ start: nextStart, end: nextEnd })
  }

  // dark:from-customSurface dark:via-customSurfaceMuted dark:to-customSurfaceMuted

  return (
    <div className="flex flex-col gap-6">
      <header className="rounded-md border border-customSurface/10 bg-gradient-to-br from-white via-white to-customSky/40 p-4 shadow-xl shadow-slate-900/5 dark:from-customSurface dark:via-customSurfaceMuted dark:to-customSurfaceMuted">
        <div className={`flex flex-col ${isMobile ? 'gap-4' : 'gap-6'} lg:flex-row lg:items-end lg:justify-between`}>
          <div className="space-y-2">
            <p className="inline-flex items-center gap-2 rounded-full bg-customAccentBlue/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide dark:text-customAccent text-customAccentBlue">
              Historical ledger
            </p>
            <h2 className="text-2xl font-semibold text-customInk dark:text-customHeading">
              Transaction history
            </h2>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Browse every deposit and withdrawal processed through payment accounts. Quickly audit older transfers with powerful filters.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="relative w-full sm:max-w-xs">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  placeholder="Search transactions"
                  onChange={handleSearchChange}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-customPrimaryText shadow-inner focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-white/10 dark:bg-customSurfaceMuted dark:text-customHeading"
                />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {typeFilters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => handleTypeFilterChange(filter)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${typeFilter.value === filter.value
                      ? 'border-transparent bg-customAccentBlue dark:bg-customAccent text-white shadow-sm'
                      : 'border-customAccent/30 text-customAccentBlue dark:text-customAccent hover:border-customAccentBlue/60 dark:hover:border-customAccent/60'
                    }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className='mt-6 flex justify-between items-center'>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-customSecondaryText dark:bg-customSurfaceMuted/60 dark:text-customMuted"
            onClick={() => handleDateRangeChange({ label: 'Custom range', value: 'custom' })}
          >
            <FunnelIcon className="h-4 w-4" />
            Date range
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {rangeFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => handleDateRangeChange(filter)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${rangeFilter.value === filter.value
                    ? 'bg-customAccentBlue dark:bg-customAccent text-white shadow-sm'
                    : 'bg-white text-customPrimaryText hover:bg-slate-100 dark:bg-customSurfaceMuted dark:text-customHeading dark:hover:bg-customSurfaceMuted/70'
                  }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {rangeFilter.value === 'custom' && (
            <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
              <div className="flex w-full max-w-xs flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-customSecondaryText dark:text-customMuted">
                  Start date
                </span>
                <DatePicker
                  date={startDate || ''}
                  setDate={(value) => handleCustomDateChange('start', value)}
                />
              </div>
              <div className="flex w-full max-w-xs flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-customSecondaryText dark:text-customMuted">
                  End date
                </span>
                <DatePicker
                  date={endDate || ''}
                  setDate={(value) => handleCustomDateChange('end', value)}
                />
              </div>
            </div>
          )}
        </div>
          <div className='flex items-center gap-3'>
            <label className='text-xs font-semibold text-customPrimaryText'>Status : </label>
            <select
              value={statusFilter.value}
              onChange={(event) => {
                const nextFilter =
                  statusFilters.find((filter) => filter.value === event.target.value) ??
                  statusFilters[0]
                handleStatusFilterChange(nextFilter)
              }}
              className="rounded-md border border-customAccent/30 bg-white px-3 py-1 text-xs font-semibold text-customAccentBlue shadow-sm outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/20 dark:bg-customSurfaceMuted dark:text-customAccent dark:focus:border-customAccent"
            >
              {statusFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex gap-3 h-64 items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white text-sm text-slate-500 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/30 dark:text-customMuted">
           <MdDataSaverOff className="h-6 w-6 animate-spin-fast"/>Loading history…
        </div>
        ) : (
          <TransactionHistoryTable
            transactions={paginatedTransactions}
            page={page}
            pageSize={pageSize}
            totalItems={filteredTransactions.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isMobile={isMobile}
          />
        )}
    </div>
  )
}

export default TransactionHistoryPage
